import { AlertTriangle, LoaderCircle, XCircle } from "lucide-react";
import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from "react";
import { statusLabels } from "../format";

export function PageHeader({ eyebrow, title, description, action }: { eyebrow: string; title: string; description: string; action?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <div className="eyebrow">{eyebrow}</div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action ? <div className="page-action">{action}</div> : null}
    </header>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const tone = status === "active" || status === "matched" ? "success" : status === "inactive" || status === "difference" ? "danger" : "neutral";
  return <span className={`status-badge ${tone}`}><span aria-hidden="true" />{statusLabels[status] ?? status}</span>;
}

export function LoadingState({ label = "正在读取账务数据" }: { label?: string }) {
  return <div className="state-block" role="status" aria-live="polite" aria-busy="true"><LoaderCircle className="spin" size={20} aria-hidden="true" /><span>{label}</span></div>;
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return <div className="state-block error" role="alert"><XCircle size={20} aria-hidden="true" /><span>{message}</span>{onRetry ? <button className="button secondary" onClick={onRetry}>重试</button> : null}</div>;
}

export function InlineAlert({ children, tone = "warning" }: { children: ReactNode; tone?: "warning" | "danger" | "success" }) {
  const role = tone === "danger" ? "alert" : "status";
  return <div className={`inline-alert ${tone}`} role={role} aria-live={tone === "danger" ? "assertive" : "polite"} aria-atomic="true"><AlertTriangle size={18} aria-hidden="true" />{children}</div>;
}

export function Field({ label, hint, error, messageId, children }: { label: string; hint?: string; error?: string; messageId?: string; children: ReactNode }) {
  const generatedId = useId();
  const element = isValidElement(children) ? children as ReactElement<{ id?: string }> : null;
  const controlId = element?.props.id ?? generatedId;
  const control = element ? cloneElement(element, { id: controlId }) : children;
  return <div className={`field ${error ? "invalid" : ""}`}><label htmlFor={controlId}>{label}</label>{control}{error || hint ? <small id={messageId} className={error ? "field-error" : undefined} aria-live={error ? "polite" : undefined}>{error ?? hint}</small> : null}</div>;
}

export function MoneyCell({ children, tone }: { children: ReactNode; tone?: "negative" | "positive" | "warning" }) {
  return <span className={`money ${tone ?? ""}`}>{children}</span>;
}
